import React, {
  useRef, useEffect, useState, useMemo,
} from 'react';
import {
  Grid, Typography, Box, Card, Divider, Checkbox, Button, Tooltip,
} from '@mui/material';
import BottomBar from 'features/navigation/bottomBar';
import ExportImage from 'common/components/exportImage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import {
  BLACK, POLICY_ITEM_TYPE, formatter, premiumTypeMapper, premiumAges, smokerOptions,
  doubleCurrencyFormatter, getPlanComparisonSections, COMPARISON_CLAIMS_TEXT, intFormatter,
} from 'common/Constants';
import PolicyTableInputCell from './common/policyTableInputCell';
import PolicyTableCheckboxCell from './common/policyTableCheckboxCell';
import CancelScenarioDialog from './common/cancelScenarioDialog';
import store from '../../app/store';
import { saveCIPremiums, saveHIACPremiums, saveCurrentBenefits } from './state/service';
import Spinner from '../../common/components/spinner';

export default function ComparePlans({ bottomBarFunctions, readOnly = false, comparePlansRef }) {
  const { handleCancelCreation, goToNextPage, goToPreviousPage } = bottomBarFunctions;
  const planComparisonRef = readOnly ? comparePlansRef : useRef();
  const pPercent = useSelector((state) => state.scenario.participationPercentages);
  const hospitalIndemnityObject = useSelector(
    (state) => state.scenario.hospitalIndemnityObject,
  );
  const criticalIllnessObject = useSelector(
    (state) => state.scenario.criticalIllnessObject,
  );
  const accidentObject = useSelector(
    (state) => state.scenario.accidentObject,
  );
  const HIPayoutDetail = useSelector(
    (state) => state.scenario.HIPayoutDetail,
  );
  const CIPayoutDetail = useSelector(
    (state) => state.scenario.CIPayoutDetail,
  );
  const ACPayoutDetail = useSelector(
    (state) => state.scenario.ACPayoutDetail,
  );
  const HIPremiumsInfo = useSelector(
    (state) => state.scenario.HIPremiumsInfo,
  );
  const ACPremiumsInfo = useSelector(
    (state) => state.scenario.ACPremiumsInfo,
  );
  const CIPremiumsInfo = useSelector(
    (state) => state.scenario.criticalIllnessPremiumsSectionInfo,
  );
  const relationshipFlag = useSelector((state) => state.scenario.relationshipFlag);
  const ageFlag = useSelector((state) => state.scenario.ageFlag);
  const scenarioId = useSelector((state) => state.scenario.scenarioId);
  const currentBenefits = useSelector((state) => state.scenario.currentBenefits);
  const HIProductIncluded = useSelector((state) => state.scenario.includeProductHI);
  const CIProductIncluded = useSelector((state) => state.scenario.includeProductCI);
  const ACProductIncluded = useSelector((state) => state.scenario.includeProductAC);

  const [benefitObject, setBenefitObject] = useState({
    HI: [], HI2: [], HIPremiums: [], CI: [], CIPremiums: [], AC: [], AC2: [], ACPremiums: [],
  });
  const [isExitDialogOpen, toggleExitDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const methods = useForm({
    defaultValues: {
      ...HIPremiumsInfo.currentPremiums,
      ...ACPremiumsInfo.currentPremiums,
      ...CIPremiumsInfo.currentPremiums,
      ...currentBenefits.formFields,
    },
    mode: 'onChange',
  });

  const { handleSubmit, formState } = methods;
  const { dirtyFields } = useFormState({ control: methods.control });

  const planComparisonSections = useMemo(() => getPlanComparisonSections({
    pPercent,
    HIPremiumsInfo,
    CIPremiumsInfo,
    ACPremiumsInfo,
  }), [pPercent, relationshipFlag, ageFlag]);

  const getProposedBenefit = (benefit) => {
    const proposedBenefit = benefit.benefitAmount || 0;
    const proposedBenefitOpen = benefit.benefitAmountOpen || 0;
    if ('type' in benefit) {
      if (benefit.type === POLICY_ITEM_TYPE.ORIGINAL) {
        return formatter.format(proposedBenefit);
      } if (benefit.type === POLICY_ITEM_TYPE.PERIOD) {
        return `${formatter.format(proposedBenefit)} / ${benefit.maximumDay} Days`;
      } if (benefit.type === POLICY_ITEM_TYPE.PERCENTAGE) {
        return `${proposedBenefit}%`;
      } if (benefit.type === POLICY_ITEM_TYPE.FRACT) {
        return `${formatter.format(proposedBenefit)} / ${formatter.format(proposedBenefitOpen)}`;
      }
    }
    return null;
  };

  useEffect(() => {
    const tempList1 = [];
    const tempList1Low = [];
    const tempList2 = [];
    const tempList3 = [];
    if (HIProductIncluded) {
      HIPayoutDetail.forEach((item) => {
        if (item.code !== '' && hospitalIndemnityObject[`${item.name}Checked`] && 'payout' in item) {
          const benefit1 = hospitalIndemnityObject[item.name];
          const benefit2 = hospitalIndemnityObject[`${item.name}2`];
          tempList1.push({
            ...item,
            benefitAmount: benefit1,
            noOfClaims:
                Math.round((pPercent.HI.participationPerc1 / 100) * item.count.TOTAL) || 0,
            isBenefit2: false,
          });
          tempList1Low.push({
            ...item,
            benefitAmount: benefit2,
            noOfClaims:
                Math.round((pPercent.HI.participationPerc2 / 100) * item.count.TOTAL) || 0,
            isBenefit2: true,
          });
        }
      });
      setBenefitObject((prev) => ({
        ...prev,
        HI: tempList1,
        HI2: tempList1Low,
        HIPremiums: HIPremiumsInfo.premiums.map((el) => ({
          ...el,
          displayName: premiumTypeMapper[el.relationship],
        })),
      }));
    }
    if (CIProductIncluded) {
      const generalCIList = [];
      CIPayoutDetail.forEach((item) => {
        if (item.code !== '' && criticalIllnessObject[`${item.name}Checked`] && 'payout' in item) {
          tempList2.push({
            ...item,
            benefitAmount: criticalIllnessObject[item.name],
            noOfClaims: item.count.TOTAL,
          });
        } else if (item.code !== '' && criticalIllnessObject[`${item.name}Checked`] && criticalIllnessObject[`${item.name}`]) {
          generalCIList.push({ ...item, benefitAmount: criticalIllnessObject[item.name] });
        }
      });
      const CIPremiums = [];
      premiumAges.forEach((age, i) => {
        CIPremiums.push({
          displayName: i === 0 ? smokerOptions.find(({ code }) => code === CIPremiumsInfo.premiumTypeCI)?.text : '',
          age,
          premium: CIPremiumsInfo.premiums.find(
            (el) => el.age === +age && !el.smoker,
          )?.premium ?? 0,
          premium2: CIPremiumsInfo.premiums.find(
            (el) => el.age === +age && el.smoker,
          )?.premium ?? 0,
        });
      });
      setBenefitObject((prev) => ({
        ...prev,
        CI: tempList2,
        CIPremiums,
      }));
    }
    if (ACProductIncluded) {
      const tempList3Low = [];
      ACPayoutDetail.forEach((item) => {
        if (item.code !== '' && accidentObject[`${item.name}Checked`] && 'payout' in item) {
          if (item.type === POLICY_ITEM_TYPE.FRACT) {
            tempList3.push({
              ...item,
              benefitAmount: accidentObject[`${item.name}Closed`],
              benefitAmountOpen: accidentObject[`${item.name}Open`],
              noOfClaims:
                Math.round((pPercent.AC.participationPerc1 / 100) * item.count.TOTAL) || 0,
              isBenefit2: false,
            });
            tempList3Low.push({
              ...item,
              benefitAmount: accidentObject[`${item.name}Closed2`],
              benefitAmountOpen: accidentObject[`${item.name}Open2`],
              noOfClaims:
                Math.round((pPercent.AC.participationPerc2 / 100) * item.count.TOTAL) || 0,
              isBenefit2: true,
            });
          } else {
            const benefit1 = accidentObject[item.name];
            const benefit2 = accidentObject[`${item.name}2`];
            tempList3.push({
              ...item,
              benefitAmount: benefit1,
              noOfClaims:
                Math.round((pPercent.AC.participationPerc1 / 100) * item.count.TOTAL) || 0,
              isBenefit2: false,
            });
            tempList3Low.push({
              ...item,
              benefitAmount: benefit2,
              noOfClaims:
                Math.round((pPercent.AC.participationPerc2 / 100) * item.count.TOTAL) || 0,
              isBenefit2: true,
            });
          }
        }
      });
      setBenefitObject((prev) => ({
        ...prev,
        AC: tempList3,
        AC2: tempList3Low,
        ACPremiums: ACPremiumsInfo.premiums.map((el) => ({
          ...el,
          displayName: premiumTypeMapper[el.relationship],
        })),
      }));
    }
    setIsLoading(false);
  }, []);

  const getCurrentPlanName = (policy, item) => {
    if (policy.isPremiums) {
      return policy.code === 'CI' ? `CI${item.age}PremiumCurrentChecked` : `${policy.code}${item.relationship}PremiumCurrentChecked`;
    }
    return policy.highLowCode.endsWith('2') ? `${item.name}${item.policyCode}Checked2` : `${item.name}${item.policyCode}Checked`;
  };

  const getCurrentInputName = (policy, item) => {
    if (policy.isPremiums) {
      return policy.code === 'CI' ? `CI${item.age}PremiumCurrent` : `${policy.code}${item.relationship}PremiumCurrent`;
    }
    return policy.highLowCode.endsWith('2') ? `${item.name}${item.policyCode}2` : `${item.name}${item.policyCode}`;
  };

  const applyProposedToCurrent = (policy) => {
    let name;
    let value;
    benefitObject[policy.highLowCode].forEach((item) => {
      if (policy.isPremiums) {
        name = policy.code === 'CI' ? `CI${item.age}PremiumCurrent` : `${policy.code}${item.relationship}PremiumCurrent`;
        value = item.premium;
        if (policy.isSmoker) {
          methods.setValue(`${name}2`, item.premium2, { shouldDirty: true });
        }
      } else {
        name = policy.highLowCode.endsWith('2') ? `${item.name}${item.policyCode}2` : `${item.name}${item.policyCode}`;
        value = item.benefitAmount;
      }
      methods.setValue(name, value, { shouldDirty: true });
    });
  };

  const getIsComparePlansBenefit2 = (policy, item) => {
    if (policy.isPremiums && ['HI', 'AC'].includes(policy.code)) {
      return ['ES', 'EC', 'EF'].includes(item.relationship);
    }
    return policy.highLowCode.endsWith('2');
  };

  const handleSaveClick = (formValues) => {
    const isDirtyPremiumCI = Object.keys(dirtyFields).some((key) => key.startsWith('CI') && key.includes('Premium'));
    const isDirtyPremiumHIAC = Object.keys(dirtyFields)
      .some((key) => (key.startsWith('HI') || key.startsWith('AC')) && key.includes('Premium'));
    const isDirtyCurrentBenefit = Object.keys(dirtyFields).filter((key) => !key.includes('Premium')).length > 0;

    if (isDirtyPremiumCI) {
      let CICurrentPremiumPayload = [];
      CICurrentPremiumPayload = CIPremiumsInfo.premiums.map((el) => ({
        ...el,
        currentPremium: el.smoker ? (+formValues[`CI${el.age}PremiumCurrent2`] || 0)
          : (+formValues[`CI${el.age}PremiumCurrent`] || 0),
        currentPremiumChecked: formValues[`CI${el.age}PremiumCurrentChecked`],
      }));
      if (CIPremiumsInfo.premiumTypeCI === 'UNISMOKER') {
        CICurrentPremiumPayload = CICurrentPremiumPayload.filter((el) => !el.smoker);
      }
      store.dispatch(saveCIPremiums({
        includePremiumCI: CIPremiumsInfo.includePremiumCI,
        premiumTypeCI: CIPremiumsInfo.premiumTypeCI,
        premiums: CICurrentPremiumPayload,
        scenarioId,
      }));
    }

    if (isDirtyPremiumHIAC) {
      const HICurrentPremiumPayload = HIPremiumsInfo.premiums.map((el) => ({
        ...el,
        currentPremium: +formValues[`HI${el.relationship}PremiumCurrent`] || 0,
        currentPremiumChecked: formValues[`HI${el.relationship}PremiumCurrentChecked`],
      }));
      const ACCurrentPremiumPayload = ACPremiumsInfo.premiums.map((el) => ({
        ...el,
        currentPremium: +formValues[`AC${el.relationship}PremiumCurrent`] || 0,
        currentPremiumChecked: formValues[`AC${el.relationship}PremiumCurrentChecked`],
      }));
      store.dispatch(saveHIACPremiums({
        includePremiumHI: HIPremiumsInfo.includePremiumHI,
        includePremiumAC: ACPremiumsInfo.includePremiumAC,
        premiums: [...HICurrentPremiumPayload, ...ACCurrentPremiumPayload],
        scenarioId,
      }));
    }

    if (isDirtyCurrentBenefit) {
      const benefits = [...benefitObject.HI, ...benefitObject.HI2,
        ...benefitObject.CI, ...benefitObject.AC, ...benefitObject.AC2];
      const benefitMap = {};
      currentBenefits.benefitsList.forEach((cb) => {
        const uniqueKey = `${cb.policyInfoName}${cb.policyCode}:${cb.isBenefitTwo}`;
        benefitMap[uniqueKey] = cb;
      });
      const cbPayload = [];
      benefits.forEach((el) => {
        const baseName = `${el.name}${el.policyCode}`;
        const benefitAmount = el.isBenefit2 ? +formValues[`${baseName}2`] : +formValues[`${baseName}`];
        const benefitChecked = el.isBenefit2 ? formValues[`${baseName}Checked2`] : formValues[`${baseName}Checked`];
        const existing = benefitMap[`${baseName}:${el.policyCode === 'CI' ? 'null' : el.isBenefit2}`];
        if (!Number.isNaN(benefitAmount) && benefitChecked !== undefined) {
          cbPayload.push({
            ...(existing?.id && { id: existing.id }),
            policyInfoName: el.name,
            benefitAmount,
            benefitChecked,
            policyCode: el.policyCode,
            scenarioId,
            isBenefitTwo: el.isBenefit2,
          });
        }
      });
      store.dispatch(saveCurrentBenefits(cbPayload));
    }
    methods.reset(formValues);
  };

  const getCurrBenefitText = (policy, item) => {
    const inputValue = Number(methods.getValues(getCurrentInputName(policy, item)) || 0);
    if (policy.isPremiums) {
      return doubleCurrencyFormatter.format(inputValue);
    }
    if (item.type === 'PERCENTAGE') {
      return `${inputValue}%`;
    }
    return formatter.format(inputValue);
  };

  const comparePlansStyles = {
    pl: readOnly ? 2 : 3,
    pr: readOnly ? 0 : 3,
    py: 3,
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{
        minHeight: '100vh',
      }}
      >
        <Grid
          container
          spacing={2}
          sx={comparePlansStyles}
        >
          {!readOnly && (
          <Grid item xs={12}>
            <Typography variant="h4">
              Compare Plans
            </Typography>
          </Grid>
          )}
          {!readOnly && (
          <Grid item xs={12}>
            <Typography variant="body1">
              This page displays the Proposed insurance plan and allows the broker to enter details
              about the Client’s Current plan. By comparing these plans, brokers can identify key
              differences and make informed recommendations for their clients.
            </Typography>
          </Grid>
          )}
          {isLoading ? <Spinner /> : (
            <Grid item xs={12}>
              <Card ref={planComparisonRef}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    position: 'relative',
                    height: '100%',
                  }}
                >
                  <Grid item xs={12} container variant="addPaddingBottom" justifyContent="space-between">
                    <Typography variant="h5">
                      {readOnly ? 'Compare Plans' : 'Plan Comparison'}
                    </Typography>
                    <ExportImage sectionRef={planComparisonRef} sectionName="plan_comparison" />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid container spacing={2} margin={0} marginTop={3}>
                    {(HIProductIncluded || CIProductIncluded || ACProductIncluded) ? (
                      <>
                        {planComparisonSections.map((policy) => (
                          benefitObject[policy.highLowCode]?.length > 0 && !policy.isHidden
                            ? (
                              <>
                                <Grid item container xs={12}>
                                  <Grid item xs={policy.isSmoker ? 2 : 4}>
                                    <Typography variant="h6" color={BLACK}>
                                      {policy.displayName}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={1}>
                                    <Typography variant="h6" color={BLACK} align="center">
                                      Current Plan
                                    </Typography>
                                  </Grid>
                                  {(policy.code === 'CI' && policy.isPremiums) ? (
                                    <Grid item xs={policy.isSmoker ? 1 : 2}>
                                      <Typography variant="h6" color={BLACK} align="center">
                                        Age
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  <Grid item xs={2}>
                                    <Typography variant="h6" color={BLACK} align="center">
                                      {policy.isPremiums ? `Current Premium${policy.isSmoker ? ' - Nonsmoker' : ''}` : 'Current Benefit'}
                                    </Typography>
                                  </Grid>
                                  {!policy.isPremiums ? (
                                    <Grid item xs={2}>
                                      <Typography variant="h6" color={BLACK} align="center">
                                        Claims
                                        <Tooltip
                                          sx={{ position: 'relative', top: '0.3rem', left: '0.3rem' }}
                                          title={COMPARISON_CLAIMS_TEXT}
                                          data-html2canvas-ignore
                                        >
                                          <InfoOutlinedIcon fontSize="small" />
                                        </Tooltip>
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  {policy.isSmoker ? (
                                    <Grid item xs={2}>
                                      <Typography variant="h6" color={BLACK} align="center">
                                        Current Premium - Smoker
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  {policy.isSmoker ? (
                                    <Grid item xs={2}>
                                      <Typography variant="h6" color={BLACK} align="center">
                                        Proposed Premium - Nonsmoker
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  {!policy.isPremiums ? (
                                    <Grid item xs={1}>
                                      <Typography variant="h6" color={BLACK} align="center">
                                        Proposed Plan
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  <Grid item xs={2}>
                                    <Typography variant="h6" color={BLACK} align="center">
                                      {policy.isPremiums ? `Proposed Premium${policy.isSmoker ? ' - Smoker' : ''}` : 'Proposed Benefit'}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12} marginTop={-1.5}>
                                  <Divider />
                                </Grid>
                                {benefitObject[policy.highLowCode]?.map((item) => (
                                  <Grid item container xs={12}>
                                    <Grid item xs={policy.isSmoker ? 2 : 4} alignSelf="center">
                                      <Typography>
                                        {item.displayName}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={1} justifyItems="center" alignSelf="center">
                                      <PolicyTableCheckboxCell
                                        name={getCurrentPlanName(policy, item)}
                                        onChange={() => {}}
                                        isComparePlans
                                        isDisabled={readOnly}
                                      />
                                    </Grid>
                                    {(policy.code === 'CI' && policy.isPremiums) ? (
                                      <Grid item xs={policy.isSmoker ? 1 : 2} align="center" alignSelf="center">
                                        <Typography>
                                          {item.age}
                                        </Typography>
                                      </Grid>
                                    ) : null}
                                    <Grid item xs={2} sx={{ pl: 2, pr: 2 }} align="center" alignSelf="center">
                                      {readOnly ? (
                                        <Typography>
                                          {getCurrBenefitText(policy, item)}
                                        </Typography>
                                      ) : (
                                        <PolicyTableInputCell
                                          name={getCurrentInputName(policy, item)}
                                          onChange={() => {}}
                                          adornment={item.type === 'PERCENTAGE' ? 'percent' : 'dollar'}
                                          defaultValue="0"
                                          isBenefit2={
                                             getIsComparePlansBenefit2(policy, item)
                                           }
                                          isComparePlans
                                          isPremiumAmountField={policy.isPremiums}
                                        />
                                      )}
                                    </Grid>
                                    {readOnly && policy.isSmoker ? (
                                      <Grid item xs={2} sx={{ pl: 2, pr: 2 }} align="center" alignSelf="center">
                                        <Typography>
                                          {doubleCurrencyFormatter.format(
                                            Number(methods.getValues(`CI${item.age}PremiumCurrent2`) || 0),
                                          )}
                                        </Typography>
                                      </Grid>
                                    ) : null}
                                    {!readOnly && policy.isSmoker ? (
                                      <Grid item xs={2} sx={{ pl: 2, pr: 2 }}>
                                        <PolicyTableInputCell
                                          name={`CI${item.age}PremiumCurrent2`}
                                          onChange={() => {}}
                                          adornment="dollar"
                                          defaultValue="0"
                                          isComparePlans
                                          isPremiumAmountField={policy.isPremiums}
                                        />
                                      </Grid>
                                    ) : null}
                                    {!policy.isPremiums ? (
                                      <Grid item xs={2} align="center" alignSelf="center">
                                        <Typography>
                                          {intFormatter.format(item.noOfClaims)}
                                        </Typography>
                                      </Grid>
                                    ) : null}
                                    {policy.isSmoker ? (
                                      <Grid item xs={2} alignSelf="center">
                                        <Typography align="center">
                                          {doubleCurrencyFormatter.format(item.premium)}
                                        </Typography>
                                      </Grid>
                                    ) : null}
                                    {!policy.isPremiums ? (
                                      <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                        <Checkbox
                                          checked
                                          disabled
                                        />
                                      </Grid>
                                    ) : null}
                                    <Grid item xs={2} alignSelf="center">
                                      <Typography align="center">
                                        {policy.isPremiums
                                          ? doubleCurrencyFormatter.format(item[`premium${policy.isSmoker ? '2' : ''}`])
                                          : getProposedBenefit(item) ?? 0}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                ))}
                                {!readOnly && (
                                <Grid item container xs={12} justifyContent="flex-end" marginBottom={4}>
                                  <Grid item xs={2} sx={{ textAlign: 'right', marginRight: 2 }}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => applyProposedToCurrent(policy)}
                                    >
                                      Apply Proposed
                                    </Button>
                                  </Grid>
                                </Grid>
                                )}
                              </>
                            ) : null
                        ))}
                      </>
                    ) : (
                      <Grid item xs={12} container justifyContent="center" alignItems="center">
                        <Typography variant="body1">No product(s) to display</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          )}
        </Grid>
        <CancelScenarioDialog
          isOpen={isExitDialogOpen}
          leavingPage={handleCancelCreation}
          sourceScreen="comparePlans"
          onClose={() => toggleExitDialog(false)}
          isExit
        />

        <Grid item xs={12}>
          <BottomBar
            showExport
            showSaveButton
            firstButtonDisplayName="EXIT"
            secondButtonDisplayName="GO TO OUTPUT"
            onClickFirstButton={() => {
              if (Object.keys(formState.dirtyFields).length) {
                toggleExitDialog(true);
              } else {
                handleCancelCreation();
              }
            }}
            onClickSecondButton={goToNextPage}
            isBackButton
            onClickBackButton={goToPreviousPage}
            onClickSaveButton={handleSubmit(handleSaveClick)}
            isSaveButtonDisabled={!formState.isValid || !formState.isDirty}
          />
        </Grid>
      </Box>
    </FormProvider>
  );
}
